import { clientSDK } from "@istari-frontend/api-client";
import { type QueryClient, queryOptions } from "@tanstack/react-query";

import { systemsQueryKeys } from "~/api/useSystems";
import { DEFAULT_CACHE_TIME } from "~/constants";

import { toSystem } from "./adapter/to-system";

export const systemQueryKeys = {
  all: ["system"] as const,
  detail: (systemId: string) => [...systemQueryKeys.all, systemId] as const,
  baseline: (systemId: string) => [...systemQueryKeys.all, systemId, "baseline"] as const,
  tag: (tagId: string) => [...systemQueryKeys.all, "tag", tagId] as const,
  snapshot: (snapshotId: string) => [...systemQueryKeys.all, "snapshot", snapshotId] as const,
};

export const systemQueryOptions = (systemId: string) =>
  queryOptions({
    queryKey: systemQueryKeys.detail(systemId),
    queryFn: () => clientSDK.getSystem(systemId),
    select: toSystem,
    staleTime: DEFAULT_CACHE_TIME,
  });

export async function fetchSystemBaselineSnapshotId(queryClient: QueryClient, systemId: string): Promise<string> {
  const baseline = await queryClient.fetchQuery(systemBaselineQueryOptions(systemId));
  return baseline.snapshotId;
}

export async function fetchTagSnapshotId(queryClient: QueryClient, tagId: string): Promise<string> {
  const tag = await queryClient.fetchQuery(tagQueryOptions(tagId));
  return tag.snapshotId;
}

export async function fetchSnapshotConfigurationId(queryClient: QueryClient, snapshotId: string): Promise<string> {
  const snapshot = await queryClient.fetchQuery(snapshotQueryOptions(snapshotId));
  return snapshot.configurationId;
}

export async function fetchConfigurationEmptyFolders(
  queryClient: QueryClient,
  configurationId: string,
): Promise<readonly string[]> {
  // The download caller reports its own failures; the flag stays off the shared options so the
  // legacy hook's unobserved reads are still reported by the query cache.
  const configuration = await queryClient.ensureQueryData({
    ...configurationQueryOptions(configurationId),
    meta: { reportsOwnErrors: true },
  });
  return configuration.emptyFolders ?? [];
}

// Shares its key and raw cache shape with the legacy `useConfigurationEmptyFolders`.
const configurationQueryOptions = (configurationId: string) =>
  queryOptions({
    queryKey: systemsQueryKeys.configuration(configurationId),
    queryFn: () => clientSDK.getConfiguration(configurationId),
  });

// Each head is read fresh and once per call; the caller classifies and reports a failure, so the cache must not.
const ONE_SHOT_READ = { staleTime: 0, meta: { reportsOwnErrors: true } } as const;

const systemBaselineQueryOptions = (systemId: string) =>
  queryOptions({
    queryKey: systemQueryKeys.baseline(systemId),
    queryFn: () => clientSDK.getSystemBaseline(systemId),
    ...ONE_SHOT_READ,
  });

const tagQueryOptions = (tagId: string) =>
  queryOptions({
    queryKey: systemQueryKeys.tag(tagId),
    queryFn: () => clientSDK.getTag(tagId),
    ...ONE_SHOT_READ,
  });

const snapshotQueryOptions = (snapshotId: string) =>
  queryOptions({
    queryKey: systemQueryKeys.snapshot(snapshotId),
    queryFn: () => clientSDK.getSnapshot(snapshotId),
    ...ONE_SHOT_READ,
    // A snapshot never changes once taken, so one read serves every later lookup.
    staleTime: Infinity,
  });
